import { useEffect, useState } from "react";
import FullScreenLoading from "../../../../Components/Loading/FullScreenLoading";
import TextInput from "../../../../Components/TextInput";
import Button from "../../../../Components/Button";
import { IDeltaHedgeExeCommonConfigDTO } from "../../../../Interfaces/deltaHedgeExe";
import {
  getDeltaHedgeExeAdjustmentDeltaGammaInputAdjustmentConfig,
  setDeltaHedgeExeAdjustmentDeltaGammaInputAdjustmentConfig,
} from "../../../../Requests/deltaHedgeExe";
import { packetErrorHandler } from "../../../../Utils/requestUtils";
import { toast } from "react-toastify";
import { IAdjustment2Option } from "../../../../Interfaces/deltaHedgeAdjustment";

interface IProps {
  isModalOpen: boolean;
  centralConfig: IDeltaHedgeExeCommonConfigDTO;
  onClose: () => void;
}

const DeltaGammaInput = ({ isModalOpen, onClose, centralConfig }: IProps) => {
  const [formValues, setFormValues] = useState(
    {} as IDeltaHedgeExeCommonConfigDTO
  );
  const [loading, setLoading] = useState(false);

  const commonChangeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const changeHandler = (name: string, value: any) => {
    const nameParts = name.split(".");
    setFormValues({
      ...formValues,
      [nameParts[0]]: {
        ...formValues[
          nameParts[0] as Exclude<
            keyof IDeltaHedgeExeCommonConfigDTO,
            "goalDeltaTolerance" | "goalGammaTolerance"
          >
        ],
        [nameParts[1]]: value,
      },
    });
  };

  useEffect(() => {
    if (!isModalOpen) return;
    setLoading(true);
    getDeltaHedgeExeAdjustmentDeltaGammaInputAdjustmentConfig()
      .then((resp) => {
        setFormValues(resp);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }, [isModalOpen]);

  const submitHandler = () => {
    setLoading(true);
    setDeltaHedgeExeAdjustmentDeltaGammaInputAdjustmentConfig(formValues)
      .then((resp) => {
        if (resp.data) {
          onClose();
          toast.success(resp.msg);
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const getExtraText = (
    objectName: Exclude<
      keyof IDeltaHedgeExeCommonConfigDTO,
      "goalDeltaTolerance" | "goalGammaTolerance"
    >,
    field: keyof IAdjustment2Option
  ) =>
    parseFloat(formValues[objectName]?.[field]?.toString() || "0") +
    parseFloat(centralConfig[objectName]?.[field]?.toString() || "0");

  const getForm = (
    title: string,
    fieldName: Exclude<
      keyof IDeltaHedgeExeCommonConfigDTO,
      "goalDeltaTolerance" | "goalGammaTolerance"
    >
  ) => (
    <div className="grid grid-cols-6 gap-x-2 gap-y-3 w-full border-b border-gray-400 pb-3">
      <div className="font-bold">{title}</div>
      <div />
      <div />
      <div />
      <div />
      <div />
      <TextInput
        label="حداقل بازده قفل روزانه (تقویت)"
        name={`${fieldName}.minDailyLockProfitReinforcement`}
        value={formValues[
          fieldName
        ]?.minDailyLockProfitReinforcement?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "minDailyLockProfitReinforcement")}
      />
      <TextInput
        label="حداقل بازده قفل روزانه (خنثی-همجنس-هم سررسید)"
        name={`${fieldName}.minDailyLockProfitSameKind`}
        value={formValues[fieldName]?.minDailyLockProfitSameKind?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "minDailyLockProfitSameKind")}
      />
      <TextInput
        label="حداقل بازده قفل روزانه (خنثی-غیر همجنس)"
        name={`${fieldName}.minDailyLockProfitDifferentKind`}
        value={formValues[
          fieldName
        ]?.minDailyLockProfitDifferentKind?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "minDailyLockProfitDifferentKind")}
      />
      <TextInput
        label="حداقل بازده قفل روزانه (خنثی-همجنس-غیر هم سررسید)"
        name={`${fieldName}.minDailyLockProfitDifferentApplyDay`}
        value={formValues[
          fieldName
        ]?.minDailyLockProfitDifferentApplyDay?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(
          fieldName,
          "minDailyLockProfitDifferentApplyDay"
        )}
      />
      <TextInput
        label="حداقل بازده قفل روزانه (تضعیف)"
        name={`${fieldName}.minDailyLockProfitDifferentWeak`}
        value={formValues[
          fieldName
        ]?.minDailyLockProfitDifferentWeak?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "minDailyLockProfitDifferentWeak")}
      />
      <TextInput
        label="مرز حباب نرمال نسبی"
        name={`${fieldName}.relativeNormalBubbleTreshold`}
        value={formValues[fieldName]?.relativeNormalBubbleTreshold?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "relativeNormalBubbleTreshold")}
      />
      <TextInput
        label="حداقل قدرمطلق اختلاف حباب"
        name={`${fieldName}.minAbsDiffrential`}
        value={formValues[fieldName]?.minAbsDiffrential?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "minAbsDiffrential")}
      />
      <TextInput
        label="حداقل قدرمطلق دلتا"
        name={`${fieldName}.minAbsDelta`}
        value={formValues[fieldName]?.minAbsDelta?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "minAbsDelta")}
      />
      <TextInput
        label="حداقل حجم روز معاملاتی قبل"
        name={`${fieldName}.minLastTrade`}
        value={formValues[fieldName]?.minLastTrade?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "minLastTrade")}
      />
      <TextInput
        label="حداقل روز تا سررسید"
        name={`${fieldName}.minRemainingDay`}
        value={formValues[fieldName]?.minRemainingDay?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "minRemainingDay")}
      />
      <TextInput
        label="حداکثر روز تا سررسید"
        name={`${fieldName}.maxRemainingDay`}
        value={formValues[fieldName]?.maxRemainingDay?.toString()}
        onChange={changeHandler}
        extraLabel
        extraText={getExtraText(fieldName, "maxRemainingDay")}
      />
    </div>
  );

  return (
    <div className="flex flex-col justify-start items-start gap-5">
      {loading ? <FullScreenLoading /> : <></>}
      <div className="flex flex-row justify-start items-center gap-5">
        <div className="w-[200px]">
          <TextInput
            name="goalDeltaTolerance"
            value={formValues.goalDeltaTolerance?.toString()}
            label="تلورانس دلتای هدف"
            onChange={commonChangeHandler}
            extraLabel
            extraText={
              parseFloat(formValues?.goalDeltaTolerance?.toString() || "0") +
              parseFloat(centralConfig?.goalDeltaTolerance?.toString() || "0")
            }
          />
        </div>
        <div className="w-[200px]">
          <TextInput
            name="goalGammaTolerance"
            value={formValues.goalGammaTolerance?.toString()}
            label="تلورانس گاما هدف"
            onChange={commonChangeHandler}
            extraLabel
            extraText={
              parseFloat(formValues?.goalGammaTolerance?.toString() || "0") +
              parseFloat(centralConfig?.goalGammaTolerance?.toString() || "0")
            }
          />
        </div>
      </div>
      {getForm("افزایش دلتا و گاما", "incDeltaIncGamma")}
      {getForm("کاهش دلتا و گاما", "decDeltaDecGamma")}
      {getForm("کاهش دلتا و افزایش گاما", "decDeltaIncGamma")}
      {getForm("افزایش دلتا و کاهش گاما", "incDeltaDecGamma")}
      <div className="w-24 mr-auto">
        <Button text="ثبت" onClick={submitHandler} variant="contained" />
      </div>
    </div>
  );
};

export default DeltaGammaInput;
